import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {  useState } from "react";
import { MessageLogsInfo } from "IF/interfaceType";
import { useThemeMode } from "Contexts/ThemeContext";

interface MessageLogsTableProps {
  data: MessageLogsInfo[];
  tableBodyHeight: number;
  tableBodyWidth: number;
  handleCheckboxChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    id: number
  ) => void;
  handleCheckAll: (checkedData: MessageLogsInfo[], checked: boolean) => void;
  setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedBody: React.Dispatch<React.SetStateAction<string | null>>;
}

export const MessageLogsTable: React.FC<MessageLogsTableProps> = ({
  data,
  tableBodyHeight,
  tableBodyWidth,
  handleCheckboxChange,
  handleCheckAll,
  setOpenDialog,
  setSelectedBody,
}) => {
  // 各列の幅割合
  const columnWidths: Record<string, string> = {
    checkBox: "10%",
    sentAt: "25%",
    deliveryType: "15%",
    notificationType: "15%",
    recipient: "25%",
    body: "10%",
  };

  // フィルター
  const [filterDeliveryType, setFilterDeliveryType] = useState<string[]>([]);
  const [filterNotificationType, setFilterNotificationType] = useState<
    string[]
  >([]);
  const [sentAtBefore, setSentAtBefore] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const addDays = (date: Date, days: number): Date => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    nextDay.setHours(0, 0, 0, 0);
    return nextDay;
  };

  const filteredData = data?.filter((row) => {
    const deliveryTypeMatch =
      filterDeliveryType.length === 0 ||
      filterDeliveryType.includes(row.deliveryType);

    const notificationTypeMatch =
      filterNotificationType.length === 0 ||
      filterNotificationType.includes(row.notificationType);

    const dateMatch = sentAtBefore
      ? new Date(row.sentAt) < addDays(new Date(sentAtBefore), 1)
      : true;

    return deliveryTypeMatch && notificationTypeMatch && dateMatch;
  });

  //ヘッダチェックボックス状態
  const isAllChecked = filteredData?.every((row) => row.check) ?? false;
  const isSomeChecked = filteredData?.some((row) => row.check) ?? false;

  const theme = useTheme();
  const { mode } = useThemeMode();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const deliveryTypeLabels: Record<string, string> = {
    email: "メール",
    sms: "SMS",
    line: "LINE",
    direct: "ダイレクト",
  };

  const notificationTypeLabels: Record<string, string> = {
    alert: "アラート",
    connection: "通信不可",
    auth: "認証",
  };

  const handleOpen = (body: string) => {
    setOpenDialog(true);
    setSelectedBody(body);
  };

  return (
    <>
      {isMobile ? (
        // 📱 カード表示
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap", // モバイル対応
              mx: 1,
            }}
          >
            {/* ✅ すべてチェック */}
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isAllChecked}
                  indeterminate={isSomeChecked && !isAllChecked}
                  onChange={(e) =>
                    handleCheckAll(filteredData, e.target.checked)
                  }
                />
              }
              label="すべて選択"
            />

            {/* ✅ 日付フィルター */}
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                送信日
              </Typography>
              <TextField
                type="date"
                value={sentAtBefore}
                onChange={(e) => setSentAtBefore(e.target.value)}
                size="small"
                sx={{
                  backgroundColor: theme.palette.custom.sForm,
                  borderRadius: 1,
                  minWidth: 100,
                  input: {
                    fontSize: "0.75rem",
                    padding: "4px",
                    ...(mode === "dark" && {
                      "&::-webkit-calendar-picker-indicator": {
                        filter: "invert(1)",
                      },
                    }),
                  },
                }}
              />
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                まで表示
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              minHeight: tableBodyHeight - 45,
              maxHeight: tableBodyHeight - 45,
              overflowY: "auto",
            }}
          >
            {filteredData.map((row, index) => (
              <Paper
                key={index}
                sx={{
                  p: 2,
                  mb: 1,
                  mx: 1,
                  backgroundColor: theme.palette.custom.row,
                  maxWidth: "100%",
                }}
              >
                <Box display="flex" alignItems="flex-start" mb={1} gap={1}>
                  <Checkbox
                    checked={row.check}
                    onChange={(event) => handleCheckboxChange(event, row.id)}
                    sx={{ mt: 0.5 }}
                  />
                  <Box display="flex" flexDirection="column">
                    <Typography
                      variant="body2"
                      fontSize="0.75rem"
                      fontWeight="bold"
                    >
                      {row.sentAt}
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      To：{row.recipient}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2">
                  配信種別：{deliveryTypeLabels[row.deliveryType] ?? ""}
                </Typography>
                <Typography variant="body2">
                  通知種別：{notificationTypeLabels[row.notificationType] ?? ""}
                </Typography>

                <Box
                  display="flex"
                  alignItems="center"
                  mt={1}
                  gap={1}
                  sx={{ width: "20vh", ml: "auto" }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleOpen(row.body)}
                    sx={{
                      width: "20vh",
                    }}
                  >
                    送信内容を表示
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        </>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            minHeight: tableBodyHeight,
            maxHeight: tableBodyHeight,
            backgroundColor: theme.palette.custom.row,
            overflowY: "auto",
          }}
        >
          <Table
            sx={{
              "& .MuiTable-root": {
                fontSize: "0.8rem",
              },
            }}
          >
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                backgroundColor: theme.palette.custom.tableHeader,
                zIndex: 1,
                color: "white",
              }}
            >
              <TableRow
                sx={{
                  "& > th": {
                    borderBottom: "none",
                    padding: "4px 8px 0 8px",
                    marginBottom: 0,
                    height: "28px",
                  },
                }}
              >
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.checkBox,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">Check</Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.sentAt,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">送信日時</Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.deliveryType,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <Typography fontWeight="fontWeightBold">配信種別</Typography>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.notificationType,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <Typography fontWeight="fontWeightBold">通知種別</Typography>
                </TableCell>
                <TableCell
                  rowSpan={2}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.recipient,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">送信先</Typography>
                </TableCell>
                <TableCell
                  rowSpan={2}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.body,
                    color: "white",
                  }}
                >
                  <Typography fontWeight="fontWeightBold">送信内容</Typography>
                </TableCell>
              </TableRow>
              <TableRow
                sx={{
                  "& > th": {
                    borderBottom: "none",
                    padding: "0px",
                    height: "28px",
                  },
                  marginTop: "-10px",
                }}
              >
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.checkBox,
                    color: "white",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllChecked}
                    indeterminate={isSomeChecked && !isAllChecked}
                    onChange={(e) =>
                      handleCheckAll(filteredData, e.target.checked)
                    }
                    color="default"
                    aria-label="チェックボックス"
                  />
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.sentAt,
                    minWidth: 210,
                    color: "white",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "0.8rem", mr: 1 }}
                    >
                      ～
                    </Typography>
                    <TextField
                      variant="standard"
                      type="date"
                      value={sentAtBefore}
                      onChange={(e) => setSentAtBefore(e.target.value)}
                      sx={{
                        backgroundColor: theme.palette.custom.sForm,
                        borderRadius: 1,
                        width: "50%",
                        input: {
                          fontSize: "0.75rem",
                          padding: "4px",
                          ...(mode === "dark" && {
                            "&::-webkit-calendar-picker-indicator": {
                              filter: "invert(1)",
                            },
                          }),
                        },
                      }}
                      inputProps={{
                        style: { fontSize: "0.75rem" },
                      }}
                      size="small"
                      fullWidth
                    />
                  </Box>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.deliveryType,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <FormControl variant="standard" sx={{ width: "70%" }}>
                    <Select
                      multiple
                      value={filterDeliveryType}
                      onChange={(e) =>
                        setFilterDeliveryType(e.target.value as string[])
                      }
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 || selected.length === 4
                          ? "すべて"
                          : selected
                              .map((v) => deliveryTypeLabels[v])
                              .join(", ")
                      }
                      sx={{
                        backgroundColor: theme.palette.custom.sForm,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      {Object.entries(deliveryTypeLabels).map(
                        ([key, label]) => (
                          <MenuItem
                            key={key}
                            value={key}
                            sx={{ width: "50%", height: "30px" }}
                          >
                            <Checkbox
                              checked={filterDeliveryType.includes(key)}
                            />
                            <ListItemText primary={label} />
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell
                  rowSpan={1}
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    width: columnWidths.notificationType,
                    color: "white",
                    display: { xs: "none", lg: "table-cell" },
                  }}
                >
                  <FormControl variant="standard" sx={{ width: "70%" }}>
                    <Select
                      multiple
                      value={filterNotificationType}
                      onChange={(e) =>
                        setFilterNotificationType(e.target.value as string[])
                      }
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 || selected.length === 3
                          ? "すべて"
                          : selected
                              .map((v) => notificationTypeLabels[v])
                              .join(", ")
                      }
                      sx={{
                        backgroundColor: theme.palette.custom.sForm,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      }}
                    >
                      {Object.entries(notificationTypeLabels).map(
                        ([key, label]) => (
                          <MenuItem
                            key={key}
                            value={key}
                            sx={{ width: "50%", height: "30px" }}
                          >
                            <Checkbox
                              checked={filterNotificationType.includes(key)}
                            />
                            <ListItemText primary={label} />
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData?.map((row, index) => (
                <TableRow key={index}>
                  <TableCell
                    sx={{
                      width: "10%",
                      textAlign: "center",
                    }}
                    size={"small"}
                  >
                    <Checkbox
                      checked={row.check}
                      onChange={(event) => handleCheckboxChange(event, row.id)}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.sentAt,
                    }}
                  >
                    {row.sentAt}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.deliveryType,
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    {deliveryTypeLabels[row.deliveryType] ?? ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.notificationType,
                      display: { xs: "none", lg: "table-cell" },
                    }}
                  >
                    {notificationTypeLabels[row.notificationType] ?? ""}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.recipient,
                    }}
                  >
                    {row.recipient}
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      width: columnWidths.body,
                      display: { xs: "none", sm: "table-cell" },
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={() => handleOpen(row.body)}
                      sx={{
                        width: {
                          xs: "100%",
                          md: "20vh",
                        },
                      }}
                    >
                      表示
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};
export default MessageLogsTable;
